import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccountProvider, useAccount } from './contexts/AccountContext';
import LandingPage from './components/LandingPage';
import SplitCalculator from './components/SplitCalculator';
import Login from './components/Login';
import Register from './components/Register';
import PublicSignView from './components/PublicSignView';
import PendingWorksView from './components/PendingWorksView';
import MySplits from './components/MySplits';
import SplitDetailsPage from './components/SplitDetailsPage';
import Header from './components/Header';
import CollaboratorList from './components/CollaboratorList';
import VerifyEmailPage from './components/VerifyEmailPage';
import EmailVerificationCallback from './components/EmailVerificationCallback';
import AccountSetup from './components/AccountSetup';
import ManagerDashboard from './components/ManagerDashboard';
// Pro features are currently disabled - keep imports for future use
// import UpgradeSuccessPage from './components/UpgradeSuccessPage';
// import UpgradePage from './components/UpgradePage';
import MaintenancePage from './components/MaintenancePage';

// Check if maintenance mode is enabled via environment variable
const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isEmailVerified, loading } = useAuth();
  const { hasNoAccounts, isLoading: accountsLoading } = useAccount();

  if (loading || accountsLoading) {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="mt-2 text-purple-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Redirect to email verification page if not verified
  if (!isEmailVerified) {
    return <Navigate to="/verify-email" />;
  }

  // Redirect to account setup if user has no accounts
  if (hasNoAccounts) {
    return <Navigate to="/account-setup" />;
  }

  return (
    <div className="min-h-screen bg-[#18181B]">
      <Header />
      <main className="pt-0">
        {children}
      </main>
    </div>
  );
};

// Route that requires auth but NOT email verification (for verify-email page itself)
const AuthenticatedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="mt-2 text-purple-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Route for account setup - requires auth and email verification, only for users without accounts
const AccountSetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isEmailVerified, loading } = useAuth();
  const { hasNoAccounts, isLoading: accountsLoading } = useAccount();

  if (loading || accountsLoading) {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="mt-2 text-purple-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isEmailVerified) {
    return <Navigate to="/verify-email" />;
  }

  // If user already has an account, redirect to dashboard (one account per user)
  if (!hasNoAccounts) {
    return <Navigate to="/my-splits" />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isEmailVerified, loading } = useAuth();
  const { isManager, isLoading: accountsLoading } = useAccount();

  if (loading || accountsLoading) {
    return (
      <div className="min-h-screen bg-gradient-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="mt-2 text-purple-200">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // If authenticated but not verified, go to verify email page
    if (!isEmailVerified) {
      return <Navigate to="/verify-email" />;
    }
    // Redirect managers to manager dashboard, artists to my-splits
    return <Navigate to={isManager ? '/manager-dashboard' : '/my-splits'} />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      {/* Public routes - redirect to dashboard if authenticated */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <div className="min-h-screen bg-[#18181B] py-8">
              <div className="container mx-auto px-4">
                <div className="text-center mb-8">
                  <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
                    <img src="/fulllogo.png" alt="Cotune" className="h-10 w-auto mx-auto mb-4" />
                  </Link>
                  <p className="text-[#14B8A6]">Welcome back! Please sign in to continue</p>
                </div>
                <Login />
              </div>
            </div>
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <div className="min-h-screen bg-[#18181B] py-8">
              <div className="container mx-auto px-4">
                <div className="text-center mb-8">
                  <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
                    <img src="/fulllogo.png" alt="Cotune" className="h-10 w-auto mx-auto mb-4" />
                  </Link>
                  <p className="text-[#14B8A6]">Create your account to get started</p>
                </div>
                <Register />
              </div>
            </div>
          </PublicRoute>
        } 
      />
      
      {/* Public signing route - no authentication required */}
      <Route path="/sign/:token" element={<PublicSignView />} />
      
      {/* Collaborator portal - view and confirm all pending works (no auth required) */}
      <Route path="/pending/:token" element={<PendingWorksView />} />
      
      {/* Email verification routes */}
      <Route 
        path="/verify-email" 
        element={
          <AuthenticatedRoute>
            <VerifyEmailPage />
          </AuthenticatedRoute>
        } 
      />
      <Route path="/verify/:token" element={<EmailVerificationCallback />} />
      
      {/* Account setup route - after email verification, before main app */}
      <Route 
        path="/account-setup" 
        element={
          <AccountSetupRoute>
            <AccountSetup />
          </AccountSetupRoute>
        } 
      />
      
      {/* Upgrade routes - redirect to my-splits since everything is now free */}
      <Route 
        path="/upgrade-success" 
        element={<Navigate to="/my-splits" replace />}
      />
      
      {/* Upgrade page route - redirect to my-splits since everything is now free */}
      <Route 
        path="/upgrade" 
        element={<Navigate to="/my-splits" replace />}
      />
      
      {/* Protected routes - require authentication */}
      <Route 
        path="/my-splits" 
        element={
          <ProtectedRoute>
            <MySplits />
          </ProtectedRoute>
        } 
      />
      {/* Manager Dashboard */}
      <Route 
        path="/manager-dashboard" 
        element={
          <ProtectedRoute>
            <ManagerDashboard />
          </ProtectedRoute>
        } 
      />
      {/* Redirect old dashboard route to my-splits */}
      <Route path="/dashboard" element={<Navigate to="/my-splits" replace />} />
      <Route 
        path="/collaborators" 
        element={
          <ProtectedRoute>
            <CollaboratorList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/splits/:id" 
        element={
          <ProtectedRoute>
            <SplitDetailsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-split" 
        element={
          <ProtectedRoute>
            <SplitCalculator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/split" 
        element={
          <ProtectedRoute>
            <SplitCalculator />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all route - redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  // Show maintenance page if maintenance mode is enabled
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <AuthProvider>
      <AccountProvider>
        <Router>
          <AppContent />
        </Router>
      </AccountProvider>
    </AuthProvider>
  );
}

export default App;
